import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  Inject,
  Injector,
  linkedSignal,
  OnInit,
  runInInjectionContext,
  signal,
  Signal,
  TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, NgForm, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { ErrorStateMatcher } from '@angular/material/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { get, isEqual } from 'lodash';
import { combineLatest, distinctUntilChanged, Observable, shareReplay, startWith } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import {
  ActionTrackerItemsDatatableConfigurationStore,
  defaultLineOverviewColumns,
  ECheckInDisplayConfiguration,
  ECheckInLogsGrouping,
  EFormDataType,
  ELaborEfficiencyLaborTimeMode,
  ELaborEfficiencyLaborTimeUnit,
  ELineOverviewColumn,
  EOEECalculationLevel,
  ERatioWidgetDisplayType,
  ERatioWidgetOperator,
  EResultAggregation,
  EWidget,
  EWidgetRequest,
  EWidgetRequestCategory,
  EWoOEEChartType,
  EWoOEEMetricType,
  IBigNumberResultFormat,
  IFormAssociation,
  ILaborEfficiencyHourModeOptions,
  ILaborEfficiencyHourUnitOptions,
  ISqcdpShifts,
  itemsDatatableColumns,
  itemsDatatableDefaultColumns,
  LineOverviewDatatableConfigurationStore,
  TWidgetCustomParameters,
  widgetConstants,
} from 'scw-digital-boards-widgets';
import { TReducerState } from '../../../app.config';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { DashboardRefreshIntervalService } from '../../../core/services/dashboard-refresh-interval.service';
import { IsUrlValidator } from '../../../core/validators/is-url.validator';
import {
  EAdvancedFilterInputMode,
  TAdvancedFilterInputsAutoSubmit,
} from '../../../shared/components/advanced-filter/advanced-filter-inputs/advanced-filter-inputs.interface';
import { IFilterIdentity } from '../../../shared/components/advanced-filter/advanced-filter.interface';
import { ErrorMessagesComponent } from '../../../shared/components/error-messages/error-messages.component';
import { FormAssociationComponent } from '../../../shared/components/form-association/form-association.component';
import { ImageUploadComponent } from '../../../shared/components/image-upload/image-upload.component';
import { InfoMessageComponent } from '../../../shared/components/info-message/info-message.component';
import { ItemCategoryShiftConfigurationComponent } from '../../../shared/components/item-category-shift-configuration/item-category-shift-configuration.component';
import { IDropdownOption, MatDropdown } from '../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { IPageConfigurationData } from '../../../shared/components/page-configuration/page-configuration.interface';
import { ShiftSelectionComponent } from '../../../shared/components/shift-selection/shift-selection.component';
import { IGenericCrudRequestConstructionParameterFilters } from '../../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';
import { IWorkspaceModules } from '../../settings/dashboards/dashboards.interface';
import { EWidgetRequestResultType } from '../../settings/data-integrations/widget-requests/widget-requests-add-edit-dialog/widget-requests-add-edit-dialog.interface';
import {
  addEditWidgetInputConfiguration,
  addWidgetConfiguration,
  inputTypeDefaultImplementations,
} from '../dashboard-constants.class';
import {
  IActionTracker,
  IWidgetData,
  IWidgetFilterModeSave,
  IWidgetFilterSuppression,
  TFormControlValueOfInput,
} from '../dashboard-content.interface';
import { ArithmeticOperatorDropdownComponent } from './input-components/arithmetic-operators-dropdown/arithmetic-operator-dropdown.component';
import { ConfigureFiltersOfAModePanelComponent } from './input-components/configure-filters-of-a-mode-panel/configure-filters-of-a-mode-panel.component';
import { IConfigurationsOfAdvancedFilterInputs } from './input-components/configure-filters-of-a-mode-panel/configure-filters-of-a-mode-panel.interface';
import {
  DatatableConfigurationInputComponent,
  IDatatableConfigurationContext,
  IPageConfigurationBuilderParams,
} from './input-components/datatable-configuration-input/datatable-configuration-input.component';
import { EndpointParameterDropdownComponent } from './input-components/endpoint-parameter-dropdown/endpoint-parameter-dropdown.component';
import { FilterModeSelectionAndOrderingPanelComponent } from './input-components/filter-mode-selection-and-ordering-panel/filter-mode-selection-and-ordering-panel.component';
import { FormAutofillDropdownComponent } from './input-components/form-autofill-dropdown/form-autofill-dropdown.component';
import { FormDataTypeDropdownComponent } from './input-components/form-data-type-dropdown/form-data-type-dropdown.component';
import { FormDropdownComponent } from './input-components/form-dropdown/form-dropdown.component';
import { FormFieldDropdownComponent } from './input-components/form-field-dropdown/form-field-dropdown.component';
import { OpenDfPageConfigurationComponent } from './input-components/open-df-page-configuration/open-df-page-configuration.component';
import { openDfPageOptions } from './input-components/open-df-page-configuration/open-df-page-configuration.interface';
import { RatioWidgetDisplayTypeDropdownComponent } from './input-components/ratio-widget-display-type-dropdown/ratio-widget-display-type-dropdown.component';
import { ResultAggregationDropdownComponent } from './input-components/result-aggregation-dropdown/result-aggregation-dropdown.component';
import { TableMappingDropdownComponent } from './input-components/table-mapping-dropdown/table-mapping-dropdown.component';
import { ThresholdBasedColoringSelectionComponent } from './input-components/threshold-based-coloring-selection/threshold-based-coloring-selection.component';
import { WidgetRequestCategoryDropdownComponent } from './input-components/widget-request-category-dropdown/widget-request-category-dropdown.component';
import { WidgetRequestDropdownComponent } from './input-components/widget-request-dropdown/widget-request-dropdown.component';
import {
  EItemType,
  ETypeDependentInputs,
  EWidgetSortBy,
  IAddEditDialogInjectData,
  IFilterPanel,
  IWidgetBasedInput,
  TRefreshIntervalDropdownOption,
  TRefreshIntervalOption,
  TSubDashboardWindowSize,
} from './widget-add-edit-dialog.interface';
import { AddEditDialogStore } from './widget-add-edit-dialog.store';

@Component({
  imports: [
    TranslatePipe,
    MatDialogTitle,
    ReactiveFormsModule,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatDropdown,
    AsyncPipe,
    NgTemplateOutlet,
    MatFormField,
    MatInput,
    MatLabel,
    ErrorMessagesComponent,
    MatIcon,
    MatCheckbox,
    MatError,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    MatSliderThumb,
    MatSlider,
    MatCard,
    MatCardContent,
    InfoMessageComponent,
    WidgetRequestDropdownComponent,
    ArithmeticOperatorDropdownComponent,
    RatioWidgetDisplayTypeDropdownComponent,
    FormAssociationComponent,
    FormDropdownComponent,
    ImageUploadComponent,
    NgIf,
    ThresholdBasedColoringSelectionComponent,
    WidgetRequestCategoryDropdownComponent,
    ResultAggregationDropdownComponent,
    FormDataTypeDropdownComponent,
    FormFieldDropdownComponent,
    ShiftSelectionComponent,
    OpenDfPageConfigurationComponent,
    ConfigureFiltersOfAModePanelComponent,
    FilterModeSelectionAndOrderingPanelComponent,
    ItemCategoryShiftConfigurationComponent,
    DatatableConfigurationInputComponent,
    TableMappingDropdownComponent,
    FormAutofillDropdownComponent,
    EndpointParameterDropdownComponent,
  ],
  providers: [
    AddEditDialogStore,
    ActionTrackerItemsDatatableConfigurationStore,
    LineOverviewDatatableConfigurationStore,
  ],
  selector: 'app-widget-add-edit-dialog',
  styleUrl: './widget-add-edit-dialog.component.scss',
  templateUrl: './widget-add-edit-dialog.component.html',
})
export class WidgetAddEditDialogComponent<Widget extends EWidget = EWidget> implements OnInit {
  private readonly injector = inject(Injector);
  private readonly snackbar = inject(SnackbarService);
  public readonly actionTrackerItemsDatatableConfigurationStore = inject(ActionTrackerItemsDatatableConfigurationStore);
  public readonly lineOverviewDatatableConfigurationStore = inject(LineOverviewDatatableConfigurationStore);

  private readonly form = viewChild<NgForm>('editForm');
  private readonly formSingleSelectDropdown = viewChild<TemplateRef<unknown>>('formSingleSelectDropdown');
  private readonly formAutofillSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'formAutofillSingleSelectDropdown',
  );
  private readonly formAssociation = viewChild<TemplateRef<unknown>>('formAssociation');
  private readonly textInput = viewChild<TemplateRef<unknown>>('textInput');
  private readonly isDateShownDropdown = viewChild<TemplateRef<unknown>>('isDateShownDropdown');
  private readonly shiftSelectionDropdown = viewChild<TemplateRef<unknown>>('shiftSelectionDropdown');
  private readonly arithmeticOperatorDropdown = viewChild<TemplateRef<unknown>>('arithmeticOperatorDropdown');
  private readonly ratioWidgetDisplayTypeDropdown = viewChild<TemplateRef<unknown>>('ratioWidgetDisplayTypeDropdown');
  private readonly widgetRequestSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'widgetRequestSingleSelectDropdown',
  );
  private readonly widgetRequestCategorySingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'widgetRequestCategorySingleSelectDropdown',
  );
  private readonly woOEEMetricLayoutMultiSelectDropdown = viewChild<TemplateRef<unknown>>(
    'woOEEMetricLayoutMultiSelectDropdown',
  );
  private readonly woOEEChartTypeSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'woOEEChartTypeSingleSelectDropdown',
  );
  private readonly oeeCalculationLevelSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'oeeCalculationLevelSingleSelectDropdown',
  );
  private readonly checkInLogsGroupingSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'checkInLogsGroupingSingleSelectDropdown',
  );
  private readonly checkInLogsDisplayConfigurationMultiSelectDropdown = viewChild<TemplateRef<unknown>>(
    'checkInLogsDisplayConfigurationMultiSelectDropdown',
  );
  private readonly laborEfficiencyUnitDropdown = viewChild<TemplateRef<unknown>>('laborEfficiencyUnitDropdown');
  private readonly laborEfficiencyModeDropdown = viewChild<TemplateRef<unknown>>('laborEfficiencyModeDropdown');
  private readonly nextFiveWorkOrderCountSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'nextFiveWorkOrderCountSingleSelectDropdown',
  );
  private readonly nextFiveWorkOrderListFieldOneSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'nextFiveWorkOrderListFieldOneSingleSelectDropdown',
  );
  private readonly nextFiveWorkOrderListFieldTwoSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'nextFiveWorkOrderListFieldTwoSingleSelectDropdown',
  );
  private readonly nextFiveWorkOrderListFieldThreeSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'nextFiveWorkOrderListFieldThreeSingleSelectDropdown',
  );
  private readonly nextFiveWorkOrderListFieldFourSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'nextFiveWorkOrderListFieldFourSingleSelectDropdown',
  );
  private readonly urlInput = viewChild<TemplateRef<unknown>>('urlInput');
  private readonly displayIntervalInput = viewChild<TemplateRef<unknown>>('displayIntervalInput');
  private readonly showThumbnailCheckbox = viewChild<TemplateRef<unknown>>('showThumbnailCheckbox');
  private readonly fitImageToWidgetCheckbox = viewChild<TemplateRef<unknown>>('fitImageToWidgetCheckbox');
  private readonly comparisonSourceSelectDropdown = viewChild<TemplateRef<unknown>>('comparisonSourceSelectDropdown');
  private readonly fromLastNDaysInput = viewChild<TemplateRef<unknown>>('fromLastNDaysInput');
  private readonly thresholdBasedColoringSelection = viewChild<TemplateRef<unknown>>('thresholdBasedColoringSelection');
  private readonly imageUpload = viewChild<TemplateRef<unknown>>('imageUpload');
  private readonly resultAggregationDropdown = viewChild<TemplateRef<unknown>>('resultAggregationDropdown');
  private readonly formDataTypeDropdown = viewChild<TemplateRef<unknown>>('formDataTypeDropdown');
  private readonly formFieldDropdown = viewChild<TemplateRef<unknown>>('formFieldDropdown');
  private readonly datatableConfigurationInput = viewChild<TemplateRef<unknown>>('datatableConfigurationInput');
  private readonly tableMappingDropdown = viewChild<TemplateRef<unknown>>('tableMappingDropdown');
  private readonly escalationStatusSingleSelectDropdown = viewChild<TemplateRef<unknown>>(
    'escalationStatusSingleSelectDropdown',
  );
  private readonly itemCategoryShiftConfigurations = viewChild<TemplateRef<unknown>>('itemCategoryShiftConfigurations');
  private readonly excludeFutureDatesFromStatusColoringCheckbox = viewChild<TemplateRef<unknown>>(
    'excludeFutureDatesFromStatusColoringCheckbox',
  );
  private readonly endpointParameterMultiSelectDropdown = viewChild<TemplateRef<unknown>>(
    'endpointParameterMultiSelectDropdown',
  );

  private readonly sortBySingleSelectDropdown = viewChild<TemplateRef<unknown>>('sortBySingleSelectDropdown');

  public readonly sortByOptions: IDropdownOption<EWidgetSortBy>[] = [
    {
      id: EWidgetSortBy.createdAtAsc,
      name: `${this.translate.instant('field.createdAt')} - ${this.translate.instant('general.ascending')}`,
    },
    {
      id: EWidgetSortBy.createdAtDesc,
      name: `${this.translate.instant('field.createdAt')} - ${this.translate.instant('general.descending')}`,
    },
    {
      id: EWidgetSortBy.dueDateAsc,
      name: `${this.translate.instant('field.dueDate')} - ${this.translate.instant('general.ascending')}`,
    },
    {
      id: EWidgetSortBy.dueDateDesc,
      name: `${this.translate.instant('field.dueDate')} - ${this.translate.instant('general.descending')}`,
    },
    {
      id: EWidgetSortBy.eventDateAsc,
      name: `${this.translate.instant('field.eventDate')} - ${this.translate.instant('general.ascending')}`,
    },
    {
      id: EWidgetSortBy.eventDateDesc,
      name: `${this.translate.instant('field.eventDate')} - ${this.translate.instant('general.descending')}`,
    },
  ];

  public readonly formGroup = new FormGroup({
    [ETypeDependentInputs.arithmeticOperatorDropdown]: new FormControl<IDropdownOption<ERatioWidgetOperator>[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.comparisonSourceSelectDropdown]: new FormControl<IDropdownOption<string>[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.displayIntervalInput]: new FormControl<number | null>(null, [
      Validators.max(3600),
      Validators.min(1),
    ]),
    [ETypeDependentInputs.formAssociation]: new FormControl<IFormAssociation | null>(null, {
      nonNullable: false,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.formDataType]: new FormControl<IDropdownOption<EFormDataType>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.formField]: new FormControl<IDropdownOption<string>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.formSingleSelectDropdown]: new FormControl<IDropdownOption[]>([], { nonNullable: true }),
    [ETypeDependentInputs.formAutofillSingleSelectDropdown]: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.fromLastNDays]: new FormControl<number>(3, { nonNullable: true }),
    [ETypeDependentInputs.isDateShownSingleSelectDropdown]: new FormControl<IDropdownOption<boolean>[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.shiftSelectionDropdown]: new FormControl<ISqcdpShifts | null>(null, {
      nonNullable: false,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.laborEfficiencyModeDropdown]: new FormControl<ILaborEfficiencyHourModeOptions[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.laborEfficiencyUnitDropdown]: new FormControl<ILaborEfficiencyHourUnitOptions[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.nextFiveWorkOrderCountSingleSelectDropdown]: new FormControl<IDropdownOption<number>[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.nextFiveWorkOrderListFieldFourSingleSelectDropdown]: new FormControl<
      IDropdownOption<string>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.nextFiveWorkOrderListFieldOneSingleSelectDropdown]: new FormControl<
      IDropdownOption<string>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.nextFiveWorkOrderListFieldThreeSingleSelectDropdown]: new FormControl<
      IDropdownOption<string>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.nextFiveWorkOrderListFieldTwoSingleSelectDropdown]: new FormControl<
      IDropdownOption<string>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.ratioWidgetDisplayType]: new FormControl<IDropdownOption<ERatioWidgetDisplayType>[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.resultAggregation]: new FormControl<IDropdownOption<EResultAggregation>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.showThumbnailCheckbox]: new FormControl<boolean>(false, { nonNullable: true }),
    [ETypeDependentInputs.fitImageToWidgetCheckbox]: new FormControl<boolean>(false, { nonNullable: true }),
    [ETypeDependentInputs.tableMapping]: new FormControl<IDropdownOption[]>([], { nonNullable: true }),
    [ETypeDependentInputs.text]: new FormControl<string>('', {
      nonNullable: true,
    }),
    [ETypeDependentInputs.text2]: new FormControl<string>('', {
      nonNullable: true,
    }),
    [ETypeDependentInputs.url]: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, IsUrlValidator()],
    }),
    [ETypeDependentInputs.widgetRequestSingleSelectDropdown]: new FormControl<
      IDropdownOption<EWidgetRequest | number>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.widgetRequestSingleSelectDropdown2]: new FormControl<
      IDropdownOption<EWidgetRequest | number>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown]: new FormControl<
      IDropdownOption<EWidgetRequestCategory>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown2]: new FormControl<
      IDropdownOption<EWidgetRequestCategory>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.woOEEChartTypeSingleSelectDropdown]: new FormControl<IDropdownOption<EWoOEEChartType>[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.oeeCalculationLevelSingleSelectDropdown]: new FormControl<
      IDropdownOption<EOEECalculationLevel>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.checkInLogsGroupingSingleSelectDropdown]: new FormControl<
      IDropdownOption<ECheckInLogsGrouping>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.checkInLogsDisplayConfigurationMultiSelectDropdown]: new FormControl<
      IDropdownOption<ECheckInDisplayConfiguration>[]
    >([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.woOEEMetricLayoutMultiSelectDropdown]: new FormControl<IDropdownOption<EWoOEEMetricType>[]>(
      [],
      {
        nonNullable: true,
      },
    ),
    [ETypeDependentInputs.thresholdBasedColoringSelection]: new FormControl<IBigNumberResultFormat[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.escalationStatusSingleSelectDropdown]: new FormControl<IDropdownOption<string>[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.imageUpload]: new FormControl<any | null>(null, {
      nonNullable: false,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.itemCategoryShiftConfigurations]: new FormControl<any | null>(null, {
      nonNullable: false,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.excludeFutureDatesFromStatusColoringCheckbox]: new FormControl<boolean>(false, {
      nonNullable: true,
    }),
    [ETypeDependentInputs.datatableConfiguration]: new FormControl<number[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    [ETypeDependentInputs.endpointParameterMultiSelectDropdown]: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
    }),
    [ETypeDependentInputs.sortBySingleSelectDropdown]: new FormControl<IDropdownOption<EWidgetSortBy>[]>(
      this.sortByOptions.filter(({ id }) => id === EWidgetSortBy.createdAtDesc),
      {
        nonNullable: true,
      },
    ),
    h: new FormControl<number | null>(null, [Validators.max(300), Validators.min(1)]),
    isAutoScrollEnabled: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    isLastUpdatedLabelShown: new FormControl<boolean>(false, {
      nonNullable: true,
      validators: [Validators.required],
    }),
    openDfPageConfiguration: new FormControl<IDropdownOption<keyof typeof openDfPageOptions>[]>([], {
      nonNullable: true,
    }),
    refreshIntervalDropdown: new FormControl<IDropdownOption<TRefreshIntervalOption>[] | null>(null, [
      Validators.required,
    ]),
    selectedWidget: new FormControl<IDropdownOption<EWidget>[] | null>(null, [Validators.required]),
    subDashboardId: new FormControl<IDropdownOption[]>([], { nonNullable: true }),
    subDashboardWindowSize: new FormControl<IDropdownOption<TSubDashboardWindowSize>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    subtitle: new FormControl<string | null>(null, [Validators.maxLength(50)]),
    title: new FormControl<string | null>(null, [Validators.maxLength(50)]),
    w: new FormControl<number | null>(null, [Validators.max(12), Validators.min(1)]),
    x: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.max(11), Validators.min(0)],
    }),
    y: new FormControl<number | null>(null, {
      validators: [Validators.required, Validators.min(0)],
    }),
    zoom: new FormControl<number>(100, { nonNullable: true }),
    actionCreateDefaultBoardId: new FormControl<IDropdownOption<number>[] | null>(null, []),
    actionCreateDefaultItemTypeId: new FormControl<IDropdownOption<EItemType>[] | null>(null, []),
    actionCreateDefaultItemCategoryId: new FormControl<IDropdownOption<number>[] | null>(null, []),
  });

  public readonly templateMap: Record<ETypeDependentInputs, Signal<TemplateRef<unknown> | undefined>> = {
    [ETypeDependentInputs.arithmeticOperatorDropdown]: this.arithmeticOperatorDropdown,
    [ETypeDependentInputs.comparisonSourceSelectDropdown]: this.comparisonSourceSelectDropdown,
    [ETypeDependentInputs.displayIntervalInput]: this.displayIntervalInput,
    [ETypeDependentInputs.formAssociation]: this.formAssociation,
    [ETypeDependentInputs.formDataType]: this.formDataTypeDropdown,
    [ETypeDependentInputs.formField]: this.formFieldDropdown,
    [ETypeDependentInputs.formSingleSelectDropdown]: this.formSingleSelectDropdown,
    [ETypeDependentInputs.formAutofillSingleSelectDropdown]: this.formAutofillSingleSelectDropdown,
    [ETypeDependentInputs.fromLastNDays]: this.fromLastNDaysInput,
    [ETypeDependentInputs.isDateShownSingleSelectDropdown]: this.isDateShownDropdown,
    [ETypeDependentInputs.shiftSelectionDropdown]: this.shiftSelectionDropdown,
    [ETypeDependentInputs.laborEfficiencyModeDropdown]: this.laborEfficiencyModeDropdown,
    [ETypeDependentInputs.laborEfficiencyUnitDropdown]: this.laborEfficiencyUnitDropdown,
    [ETypeDependentInputs.nextFiveWorkOrderCountSingleSelectDropdown]: this.nextFiveWorkOrderCountSingleSelectDropdown,
    [ETypeDependentInputs.nextFiveWorkOrderListFieldFourSingleSelectDropdown]:
      this.nextFiveWorkOrderListFieldFourSingleSelectDropdown,
    [ETypeDependentInputs.nextFiveWorkOrderListFieldOneSingleSelectDropdown]:
      this.nextFiveWorkOrderListFieldOneSingleSelectDropdown,
    [ETypeDependentInputs.nextFiveWorkOrderListFieldThreeSingleSelectDropdown]:
      this.nextFiveWorkOrderListFieldThreeSingleSelectDropdown,
    [ETypeDependentInputs.nextFiveWorkOrderListFieldTwoSingleSelectDropdown]:
      this.nextFiveWorkOrderListFieldTwoSingleSelectDropdown,
    [ETypeDependentInputs.ratioWidgetDisplayType]: this.ratioWidgetDisplayTypeDropdown,
    [ETypeDependentInputs.resultAggregation]: this.resultAggregationDropdown,
    [ETypeDependentInputs.showThumbnailCheckbox]: this.showThumbnailCheckbox,
    [ETypeDependentInputs.fitImageToWidgetCheckbox]: this.fitImageToWidgetCheckbox,
    [ETypeDependentInputs.tableMapping]: this.tableMappingDropdown,
    [ETypeDependentInputs.text]: this.textInput,
    [ETypeDependentInputs.text2]: this.textInput,
    [ETypeDependentInputs.url]: this.urlInput,
    [ETypeDependentInputs.widgetRequestSingleSelectDropdown]: this.widgetRequestSingleSelectDropdown,
    [ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown]: this.widgetRequestCategorySingleSelectDropdown,
    [ETypeDependentInputs.widgetRequestSingleSelectDropdown2]: this.widgetRequestSingleSelectDropdown,
    [ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown2]: this.widgetRequestCategorySingleSelectDropdown,
    [ETypeDependentInputs.woOEEChartTypeSingleSelectDropdown]: this.woOEEChartTypeSingleSelectDropdown,
    [ETypeDependentInputs.oeeCalculationLevelSingleSelectDropdown]: this.oeeCalculationLevelSingleSelectDropdown,
    [ETypeDependentInputs.checkInLogsGroupingSingleSelectDropdown]: this.checkInLogsGroupingSingleSelectDropdown,
    [ETypeDependentInputs.checkInLogsDisplayConfigurationMultiSelectDropdown]:
      this.checkInLogsDisplayConfigurationMultiSelectDropdown,
    [ETypeDependentInputs.woOEEMetricLayoutMultiSelectDropdown]: this.woOEEMetricLayoutMultiSelectDropdown,
    [ETypeDependentInputs.thresholdBasedColoringSelection]: this.thresholdBasedColoringSelection,
    [ETypeDependentInputs.imageUpload]: this.imageUpload,
    [ETypeDependentInputs.escalationStatusSingleSelectDropdown]: this.escalationStatusSingleSelectDropdown,
    [ETypeDependentInputs.itemCategoryShiftConfigurations]: this.itemCategoryShiftConfigurations,
    [ETypeDependentInputs.excludeFutureDatesFromStatusColoringCheckbox]:
      this.excludeFutureDatesFromStatusColoringCheckbox,
    [ETypeDependentInputs.datatableConfiguration]: this.datatableConfigurationInput,
    [ETypeDependentInputs.endpointParameterMultiSelectDropdown]: this.endpointParameterMultiSelectDropdown,
    [ETypeDependentInputs.sortBySingleSelectDropdown]: this.sortBySingleSelectDropdown,
  };

  private readonly formControlValue = toSignal(this.formGroup.valueChanges, { initialValue: this.formGroup.value });
  public readonly typeBasedConfiguration = computed(
    () => {
      const selectedType = this.injectData.type;

      return addWidgetConfiguration[selectedType].visibleInputsInOrder.reduce(
        (reducePayload, input) => {
          if (input.type === 'filterSection') {
            return reducePayload;
          }

          const { type: inputName } = input;
          const {
            editDataKeyName,
            type: inputType,
            extractFormControlValueCustomImplementation,
          } = addEditWidgetInputConfiguration[inputName];
          const formControlValue = this.formControlValue()?.[inputName];

          const value = extractFormControlValueCustomImplementation
            ? extractFormControlValueCustomImplementation?.(formControlValue)
            : inputTypeDefaultImplementations[inputType].extractFormControlValue(formControlValue);

          return value === null
            ? reducePayload
            : {
                ...reducePayload,
                [editDataKeyName(this.injectData.type)]: value,
              };
        },
        {} as TWidgetCustomParameters[Widget],
      );
    },
    { equal: isEqual },
  );

  public readonly selectedWidgetConfiguration$ = this.formGroup.controls.selectedWidget.valueChanges.pipe(
    map((selectedWidgetValue) => {
      if (!selectedWidgetValue?.length) {
        return null;
      }

      const selectedWidget = selectedWidgetValue[0].id;

      return addWidgetConfiguration[selectedWidget];
    }),
    shareReplay({ refCount: true }),
  );

  private readonly selectedWidgetConfiguration = toSignal(this.selectedWidgetConfiguration$);

  public readonly refreshIntervalOptions$: Observable<TRefreshIntervalDropdownOption[]> =
    this.selectedWidgetConfiguration$.pipe(
      map((selectedWidgetValue) => {
        if (!selectedWidgetValue) {
          return [];
        }

        const availableRefreshIntervals = selectedWidgetValue.refreshIntervalOptions;

        return availableRefreshIntervals
          ? this.dashboardRefreshIntervalService.refreshDataIntervalOptions.filter(({ id }) =>
              availableRefreshIntervals.includes(id),
            )
          : [];
      }),
    );

  public readonly filters = this.injectData.editData?.filters ?? [];
  public readonly filtersOfWidget = computed(() => {
    return widgetConstants[this.injectData.type].filterModes({
      typeBasedConfiguration: this.typeBasedConfiguration(),
    });
  });
  public readonly modeNamesOfWidget = computed(() => {
    return this.filtersOfWidget().map(({ name }) => name);
  });
  public readonly filterModeOrder = linkedSignal<string[], string[]>({
    computation: (modeNamesOfWidget, previous) => {
      const currentValue = previous?.value ??
        this.injectData.editData?.filters.map(({ modeName }) => modeName) ?? [modeNamesOfWidget[0]];

      const allMatch = currentValue.every((modeName) => modeNamesOfWidget.includes(modeName));

      return allMatch ? currentValue : [modeNamesOfWidget[0]];
    },
    source: this.modeNamesOfWidget,
  });

  public readonly visibleInputsInOrder = computed(
    () => {
      const selectedWidgetValue = this.selectedWidgetConfiguration();

      if (!selectedWidgetValue) {
        return [] as IWidgetBasedInput[];
      }

      return selectedWidgetValue.visibleInputsInOrder.map((input): IWidgetBasedInput | 'filterSection' => {
        if (input.type === 'filterSection') {
          return 'filterSection';
        }

        return {
          formControl: this.formGroup.controls[input.type],
          template: this.templateMap[input.type]()!,
          widgetSpecificContext: this.getWidgetSpecificContext(input.type),
        };
      });
    },
    { equal: isEqual },
  );

  public readonly filterSuppression = signal<IWidgetFilterSuppression[]>(
    this.injectData.editData?.filters.map(({ modeName, suppressedFilters }) => ({ modeName, suppressedFilters })) ?? [],
  );

  public filterSectionData: IFilterPanel[] = [];

  public readonly workspaceModulePermissions: Signal<IWorkspaceModules | undefined> = this.globalStore.selectSignal(
    (state) => state.dashboardContent.dashboardData?.workspace.modulePermissions,
  );
  public readonly workspaceId = this.globalStore.selectSignal(
    (state) => state.dashboardContent.dashboardData!.workspace.id,
  );

  public readonly dashboardId = this.globalStore.selectSignal((state) => state.dashboardContent.dashboardData!.id);
  public readonly oeeCalculationLevelAccess = this.globalStore.selectSignal(
    (state) =>
      state.user.user?.dfUserConfiguration?.virtualFlags.oeeCalculationBasis &&
      state.user.user?.dfUserConfiguration?.virtualFlags.oeeBaseType,
  );

  public readonly isActionTrackerModuleAllowed: Signal<boolean> = computed(() => {
    return !!this.workspaceModulePermissions()?.actionTracker;
  });

  public readonly isOeeTrackerModuleAllowed: Signal<boolean> = computed(() => {
    return !!this.workspaceModulePermissions()?.oeeTracker;
  });

  public readonly widgetTypeOptions: IDropdownOption<EWidget>[] = Object.values(EWidget).map((widget) => ({
    id: widget,
    name: this.translate.instant(`widget.${widget}.name`),
  }));

  public readonly isDateShownOptions: IDropdownOption<boolean>[] = [
    {
      id: true,
      name: this.translate.instant('general.show'),
    },
    {
      id: false,
      name: this.translate.instant('general.hide'),
    },
  ];

  public readonly refreshIntervalDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.refreshInterval'),
  };

  public readonly isDateShownDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.isDateShown'),
  };

  public readonly woOEEMetricLayoutMultiSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: false,
    text: this.translate.instant('page.widgetContent.addEditDialog.metricLayout'),
  };

  public readonly woOEEChartTypeSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.metricChart'),
  };

  public readonly oeeCalculationLevelSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.oeeCalculationLevel'),
  };

  public readonly checkInLogsGroupingSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.checkInLogsGrouping'),
  };

  public readonly checkInLogsDisplayConfigurationMultiSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: false,
    text: this.translate.instant('page.widgetContent.addEditDialog.checkInLogsDisplayConfiguration'),
  };

  public readonly laborEfficiencyHourUnitSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.hourUnit'),
  };

  public readonly laborEfficiencyHourModeSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.hourMode'),
  };

  public readonly nextFiveWorkOrderCountSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('widget.nextFiveWorkOrder.listConfig.workOrderCount'),
  };

  public readonly nextFiveWorkOrderListFieldOneSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('widget.nextFiveWorkOrder.listConfig.firstField'),
  };

  public readonly actionCreateDefaultBoardIdConfiguration: IDropdownSettings = {
    enableSearchFilter: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.board'),
  };

  public readonly actionCreateDefaultItemTypeIdConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.itemType'),
  };

  public readonly actionCreateDefaultItemCategoryIdConfiguration: IDropdownSettings = {
    enableSearchFilter: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.itemCategory'),
  };

  public readonly subDashboardDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: true,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.subDashboard'),
  };

  public readonly subDashboardWindowSizeDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.subDashboardWindowSize'),
  };

  public readonly escalationStatusSingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.escalationStatus'),
  };

  public readonly sortBySingleSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: false,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.sortBy'),
  };

  public readonly nextFiveWorkOrderListFieldTwoSingleSelectDropdownConfiguration: IDropdownSettings = {
    ...this.nextFiveWorkOrderListFieldOneSingleSelectDropdownConfiguration,
    text: this.translate.instant('widget.nextFiveWorkOrder.listConfig.secondField'),
  };
  public readonly nextFiveWorkOrderListFieldThreeSingleSelectDropdownConfiguration: IDropdownSettings = {
    ...this.nextFiveWorkOrderListFieldOneSingleSelectDropdownConfiguration,
    text: this.translate.instant('widget.nextFiveWorkOrder.listConfig.thirdField'),
  };
  public readonly nextFiveWorkOrderListFieldFourSingleSelectDropdownConfiguration: IDropdownSettings = {
    ...this.nextFiveWorkOrderListFieldOneSingleSelectDropdownConfiguration,
    text: this.translate.instant('widget.nextFiveWorkOrder.listConfig.fourthField'),
  };

  public readonly woOEEMetricLayoutOptions: IDropdownOption<EWoOEEMetricType>[] = [
    { id: EWoOEEMetricType.oee, name: this.translate.instant('general.oee') },
    { id: EWoOEEMetricType.availability, name: this.translate.instant('general.availability') },
    { id: EWoOEEMetricType.performance, name: this.translate.instant('general.performance') },
    { id: EWoOEEMetricType.quality, name: this.translate.instant('general.quality') },
  ];

  public readonly woOEEChartTypeOptions: IDropdownOption<EWoOEEChartType>[] = [
    { id: EWoOEEChartType.combo, name: this.translate.instant('widget.woOEE.chartType.combo') },
    { id: EWoOEEChartType.bar, name: this.translate.instant('widget.woOEE.chartType.bar') },
    { id: EWoOEEChartType.donut, name: this.translate.instant('widget.woOEE.chartType.donut') },
  ];

  public readonly oeeCalculationLevelOptions: IDropdownOption<EOEECalculationLevel>[] = [
    { id: EOEECalculationLevel.oee3, name: this.translate.instant('general.oee') },
    { id: EOEECalculationLevel.oee1, name: this.translate.instant('general.oee1') },
    { id: EOEECalculationLevel.oee2, name: this.translate.instant('general.oee2') },
  ];

  public readonly checkInLogsGroupingOptions: IDropdownOption<ECheckInLogsGrouping>[] = [
    { id: ECheckInLogsGrouping.noGrouping, name: this.translate.instant('widget.checkInLogs.grouping.noGrouping') },
    { id: ECheckInLogsGrouping.groupByLine, name: this.translate.instant('widget.checkInLogs.grouping.groupByLine') },
  ];

  public readonly checkInLogsDisplayConfigurationOptions: IDropdownOption<ECheckInDisplayConfiguration>[] = [
    {
      id: ECheckInDisplayConfiguration.showUserCount,
      name: this.translate.instant('widget.checkInLogs.displayConfiguration.showUserCount'),
    },
    {
      id: ECheckInDisplayConfiguration.showAssetCount,
      name: this.translate.instant('widget.checkInLogs.displayConfiguration.showAssetCount'),
    },
  ];

  public readonly laborEfficiencyHourUnitOptions: ILaborEfficiencyHourUnitOptions[] = [
    {
      id: ELaborEfficiencyLaborTimeUnit.min,
      name: this.translate.instant('widget.laborEfficiency.hourUnit.min'),
    },
    {
      id: ELaborEfficiencyLaborTimeUnit.hour,
      name: this.translate.instant('widget.laborEfficiency.hourUnit.hour'),
    },
  ];

  public readonly laborEfficiencyHourModeOptions: ILaborEfficiencyHourModeOptions[] = [
    {
      id: ELaborEfficiencyLaborTimeMode.lineAndStation,
      name: this.translate.instant('widget.laborEfficiency.hourMode.lineAndStation'),
    },
    {
      id: ELaborEfficiencyLaborTimeMode.onlyLine,
      name: this.translate.instant('widget.laborEfficiency.hourMode.line'),
    },
    {
      id: ELaborEfficiencyLaborTimeMode.onlyStation,
      name: this.translate.instant('widget.laborEfficiency.hourMode.station'),
    },
  ];

  public readonly nextFiveWorkOrderCountOptions: IDropdownOption<number>[] = [
    { id: 1, name: '1' },
    { id: 2, name: '2' },
    { id: 3, name: '3' },
    { id: 4, name: '4' },
    { id: 5, name: '5' },
  ];

  public readonly nextFiveWorkOrderListFieldOptions: IDropdownOption<string>[] = [
    { id: 'woNumber', name: this.translate.instant('widget.nextFiveWorkOrder.listOptions.workOrderName') },
    { id: 'productId', name: this.translate.instant('widget.nextFiveWorkOrder.listOptions.productId') },
    {
      id: 'productDescription',
      name: this.translate.instant('widget.nextFiveWorkOrder.listOptions.productDescription'),
    },
    { id: 'scheduledLine', name: this.translate.instant('widget.nextFiveWorkOrder.listOptions.scheduledLine') },
    { id: 'job', name: this.translate.instant('widget.nextFiveWorkOrder.listOptions.job') },
  ];

  public readonly comparisonSourceSelectDropdownConfiguration: IDropdownSettings = {
    enableSearchFilter: false,
    hasDelayedDefaultValue: true,
    isClientSide: true,
    limitSelection: 5,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('page.widgetContent.addEditDialog.comparisonSource'),
  };

  public readonly comparisonSourceOptions: IDropdownOption<string>[] = [
    { id: 'oee', name: this.translate.instant('general.oee') },
    { id: 'availability', name: this.translate.instant('general.availability') },
    { id: 'performance', name: this.translate.instant('general.performance') },
    { id: 'quality', name: this.translate.instant('general.quality') },
    { id: 'throughput', name: this.translate.instant('general.throughput') },
  ];

  public boards: IDropdownOption<number>[] = [];
  public itemCategories: IDropdownOption<number>[] = [];
  public itemTypes: IDropdownOption<EItemType>[] = [
    {
      id: EItemType.issue,
      name: this.translate.instant('general.actionItemType.issues'),
    },
    {
      id: EItemType.action,
      name: this.translate.instant('general.actionItemType.actions'),
    },
  ];
  public readonly subDashboardWindowSizeOptions: IDropdownOption<TSubDashboardWindowSize>[] = [
    { id: EDialogWidth.small, name: 'S' },
    { id: EDialogWidth.medium, name: 'M' },
    { id: EDialogWidth.large, name: 'L' },
    { id: EDialogWidth.xl, name: 'XL' },
  ];

  public readonly escalationStatusSingleSelectDropdownOptions: IDropdownOption<string>[] = [
    { id: 'selfAndEscalated', name: this.translate.instant('page.widgetContent.addEditDialog.all') },
    { id: 'self', name: this.translate.instant('page.widgetContent.addEditDialog.onlyNotEscalated') },
    { id: 'escalated', name: this.translate.instant('page.widgetContent.addEditDialog.escalatedItems') },
  ];

  public readonly marginTopIfDisplayConfigHasError$ = combineLatest(
    (['x', 'y', 'w', 'h'] as const).map((controlName) =>
      this.formGroup.controls[controlName].events.pipe(
        map(
          () => this.form() && new ErrorStateMatcher().isErrorState(this.formGroup.controls[controlName], this.form()!),
        ),
        startWith(false),
      ),
    ),
  ).pipe(map((statuses) => (statuses.some(Boolean) ? 'mt-4' : '')));

  public isCategoryFormSubmissions$ = new Observable<boolean>().pipe(startWith(false));
  public isValueFromContentSetup$ = new Observable<boolean>().pipe(startWith(false));
  public readonly ETypeDependentInputs = ETypeDependentInputs;
  public readonly EWidget = EWidget;
  public readonly EWidgetRequestCategory = EWidgetRequestCategory;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly injectData: IAddEditDialogInjectData<Widget>,
    private readonly dashboardRefreshIntervalService: DashboardRefreshIntervalService,
    private readonly translate: TranslateService,
    private readonly dialogRef: MatDialogRef<WidgetAddEditDialogComponent<Widget>>,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public readonly store: AddEditDialogStore,
    private readonly globalStore: Store<TReducerState>,
  ) {
    this.registerDynamicInputsManagement();
    this.refreshIntervalOptions$.subscribe((options) => {
      if (options.length) {
        this.formGroup.controls.refreshIntervalDropdown.enable();
      } else {
        this.formGroup.controls.refreshIntervalDropdown.disable();
      }

      if (!this.injectData.editData && options.length) {
        const dashboardDependedOption = options.find((option) => option.id === 0);
        this.formGroup.controls.refreshIntervalDropdown.setValue([dashboardDependedOption || options[0]]);
      }
    });

    this.formGroup.controls.subDashboardId.valueChanges
      .pipe(startWith(this.formGroup.controls.subDashboardId.value), takeUntilDestroyed())
      .subscribe((value) => {
        if (value.length) {
          this.formGroup.controls.subDashboardWindowSize.enable();
        } else {
          this.formGroup.controls.subDashboardWindowSize.disable();
        }
      });
  }

  private registerDynamicInputsManagement(): void {
    this.selectedWidgetConfiguration$.subscribe((widgetConfiguration) => {
      if (!widgetConfiguration) {
        return;
      }

      const typeDependentInputs = widgetConfiguration.visibleInputsInOrder.filter(
        (visibleInput) => 'type' in visibleInput,
      );

      for (const widgetInput of Object.values(ETypeDependentInputs)) {
        const widgetBasedInputConfiguration = typeDependentInputs.find(({ type }) => type === widgetInput);

        if (!widgetBasedInputConfiguration) {
          this.formGroup.controls[widgetInput].disable();

          continue;
        }

        if (this.shouldDisableInputDueToAccess(widgetInput)) {
          this.disableAndClearControl(widgetInput);

          continue;
        }

        this.formGroup.controls[widgetInput].enable();

        if (widgetBasedInputConfiguration.type === 'filterSection') {
          continue;
        }

        const finalValidators: ValidatorFn[] = this.getControlValidators(
          widgetInput,
          widgetBasedInputConfiguration.rules,
        );
        this.formGroup.controls[widgetInput].setValidators(finalValidators);
        this.formGroup.controls[widgetInput].updateValueAndValidity();
      }
    });
  }

  private shouldDisableInputDueToAccess(widgetInput: ETypeDependentInputs): boolean {
    if (widgetInput === ETypeDependentInputs.oeeCalculationLevelSingleSelectDropdown) {
      return !Boolean(this.oeeCalculationLevelAccess());
    }

    return false;
  }

  private disableAndClearControl(widgetInput: ETypeDependentInputs): void {
    const { [widgetInput]: control } = this.formGroup.controls;

    control.disable();
    control.clearValidators();
    control.updateValueAndValidity();
  }

  private getControlValidators(widgetInput: ETypeDependentInputs, widgetSpecificRules: ValidatorFn[]): ValidatorFn[] {
    const baseRules: ValidatorFn[] = [...get(addEditWidgetInputConfiguration, `${widgetInput}.rules`, [])];

    if (
      widgetInput === ETypeDependentInputs.oeeCalculationLevelSingleSelectDropdown &&
      Boolean(this.oeeCalculationLevelAccess())
    ) {
      baseRules.push(Validators.required);
    }

    return [...baseRules, ...widgetSpecificRules];
  }

  public get isEditingPersistedWidget(): boolean {
    return typeof this.injectData.editData?.widgetId === 'number';
  }

  public ngOnInit(): void {
    this.getActionTrackerConfigurationDropdowns();

    this.formGroup.controls.selectedWidget.setValue(
      this.widgetTypeOptions.filter(({ id }) => id === this.injectData.type),
    );

    this.store.loadSubDashboards({
      additionalCustomSearch: [{ id: { $ne: this.dashboardId() } }],
      filters: [{ field: 'workspaceId', ids: [this.workspaceId()] }],
      limit: 1000,
    });

    this.prepareFormData();
    this.disableBigNumberFormRelatedFieldsConditionally();
  }

  public submit(): void {
    this.formGroup.markAllAsTouched();

    if (!this.formGroup.valid) {
      return;
    }

    if (this.modeNamesOfWidget().length > 1 && !this.filterModeOrder().length) {
      this.snackbar.open(this.translate.instant('system.message.noFilterModeSelected'), ESnackbar.error);

      return;
    }

    this.dialogRef.close(this.getFormData());
  }

  public resetToDefaultValues(): void {
    const { layout } = this.injectData;

    this.formGroup.controls.w.reset(addWidgetConfiguration[this.injectData.type].defaultSize[layout].w);
    this.formGroup.controls.h.reset(addWidgetConfiguration[this.injectData.type].defaultSize[layout].h);
    this.formGroup.controls.zoom.reset(100);
    this.formGroup.controls.isAutoScrollEnabled.reset(addWidgetConfiguration[this.injectData.type].isAutoScrollChecked);
    this.formGroup.controls.isLastUpdatedLabelShown.reset(false);
    queueMicrotask(() => this.changeDetectorRef.detectChanges());
  }

  private prepareFormData(): void {
    const { editData } = this.injectData;

    if (!editData) {
      this.resetToDefaultValues();

      this.formGroup.controls.x.removeValidators([Validators.required]);
      this.formGroup.controls.y.removeValidators([Validators.required]);
      this.initializeFilterSectionData();

      return;
    }

    const actionConfig: IActionTracker | undefined = editData.configuration?.actionTracker;

    this.formGroup.reset({
      ...Object.values(ETypeDependentInputs).reduce(
        (reducePayload, input: ETypeDependentInputs) => {
          return {
            ...reducePayload,
            [input]: addEditWidgetInputConfiguration[input].editDataFormResetCustomImplementation
              ? addEditWidgetInputConfiguration[input].editDataFormResetCustomImplementation(
                  editData.typeBasedConfiguration,
                )
              : inputTypeDefaultImplementations[addEditWidgetInputConfiguration[input].type].setFormField(
                  addEditWidgetInputConfiguration[input].editDataKeyName(this.injectData.type),
                  editData.typeBasedConfiguration,
                ),
          };
        },
        {} as {
          [Input in ETypeDependentInputs]: TFormControlValueOfInput<Input>;
        },
      ),
      actionCreateDefaultBoardId: null,
      actionCreateDefaultItemCategoryId: null,
      actionCreateDefaultItemTypeId: actionConfig?.itemTypeId
        ? this.itemTypes.filter((type) => type.id === actionConfig.itemTypeId)
        : null,
      h: editData.configuration.gridstackOptions.h,
      isAutoScrollEnabled: editData.configuration.isAutoScrollEnabled,
      isLastUpdatedLabelShown: editData.configuration.isLastUpdatedLabelShown,
      openDfPageConfiguration: editData.configuration.openDfPageConfiguration
        ? [{ id: editData.configuration.openDfPageConfiguration, name: '...' }]
        : [],
      refreshIntervalDropdown: this.dashboardRefreshIntervalService.refreshDataIntervalOptions.filter(
        ({ id }) => id === editData.configuration.refreshInterval,
      ),
      selectedWidget: this.widgetTypeOptions.filter(({ id }) => id === editData.type),
      subDashboardId: editData.subDashboardId ? [{ id: editData.subDashboardId, name: '...' }] : [],
      subDashboardWindowSize: editData.configuration.subDashboardWindowSize
        ? this.subDashboardWindowSizeOptions.filter(({ id }) => id === editData.configuration.subDashboardWindowSize)
        : [],
      subtitle: editData.subtitle,
      title: editData.title,
      w: editData.configuration.gridstackOptions.w,
      x: editData.configuration.gridstackOptions.x,
      y: editData.configuration.gridstackOptions.y,
      zoom: editData.configuration.zoom,
    });
    this.initializeFilterSectionData();
  }

  private getFormData<SelectedWidgetType extends EWidget>(): IWidgetData<SelectedWidgetType> {
    const { layout } = this.injectData;

    const selectedType = this.formGroup.controls.selectedWidget.value![0].id as SelectedWidgetType;
    const refreshInterval = get(this.formGroup.controls.refreshIntervalDropdown.value, '0.id', -1);
    const x = this.formGroup.controls.x.value;
    const y = this.formGroup.controls.y.value;
    const w = this.formGroup.controls.w.value ?? addWidgetConfiguration[this.injectData.type].defaultSize[layout].w;
    const h = this.formGroup.controls.h.value ?? addWidgetConfiguration[this.injectData.type].defaultSize[layout].h;
    const isAutoScrollEnabled =
      this.formGroup.controls.isAutoScrollEnabled.value ??
      addWidgetConfiguration[this.injectData.type].isAutoScrollChecked;

    const filterValues: IWidgetFilterModeSave[] = this.filterSectionData.map(({ modeName, filters }) => {
      const suppressedFilters =
        this.filterSuppression().find((suppression) => suppression.modeName === modeName)?.suppressedFilters ?? [];

      return {
        filters: filters
          .filter(({ submitData }) => {
            const submit = submitData();

            return submit?.data && !suppressedFilters.includes(submit.data.field);
          })
          .map(({ submitData }) => submitData()!.data!),
        modeName,
        suppressedFilters,
      };
    });

    const actionTrackerValues: IActionTracker = {
      boardId: this.formGroup.controls.actionCreateDefaultBoardId.value?.[0]?.id ?? null,
      itemCategoryId: this.formGroup.controls.actionCreateDefaultItemCategoryId.value?.[0]?.id ?? null,
      itemTypeId: this.formGroup.controls.actionCreateDefaultItemTypeId.value?.[0]?.id ?? null,
    };

    const hasActionTracker: boolean = Object.values(actionTrackerValues).some((value) => value !== null);
    const subDashboardWindowSize = this.formGroup.controls.subDashboardWindowSize.value[0]?.id;

    return {
      configuration: {
        ...(hasActionTracker && { actionTracker: actionTrackerValues }),
        gridstackOptions: {
          h: Number(h),
          layout: this.injectData.layout,
          w: Number(w),
          x: x === null ? undefined : Number(x),
          y: y === null ? undefined : Number(y),
        },
        isAutoScrollEnabled,
        isLastUpdatedLabelShown: this.formGroup.controls.isLastUpdatedLabelShown.value,
        ...(this.formGroup.controls.openDfPageConfiguration.value.length
          ? { openDfPageConfiguration: this.formGroup.controls.openDfPageConfiguration.value[0].id }
          : {}),
        refreshInterval,
        ...(subDashboardWindowSize ? { subDashboardWindowSize } : {}),
        zoom: this.formGroup.controls.zoom.value,
      },
      filters: filterValues,
      subDashboardId: this.formGroup.controls.subDashboardId.value[0]?.id ?? null,
      subtitle: this.formGroup.controls.subtitle.value || null,
      title: this.formGroup.controls.title.value || null,
      type: selectedType,
      typeBasedConfiguration: this.typeBasedConfiguration() as TWidgetCustomParameters[SelectedWidgetType],
      widgetId: this.injectData.editData?.widgetId ?? Symbol(),
    };
  }

  private getWidgetSpecificContext(type: ETypeDependentInputs): object {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (type) {
      case ETypeDependentInputs.widgetRequestSingleSelectDropdown:
      case ETypeDependentInputs.widgetRequestSingleSelectDropdown2:
        const widgetRequestCategoryFormControl =
          type === ETypeDependentInputs.widgetRequestSingleSelectDropdown
            ? this.formGroup.controls[ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown]
            : this.formGroup.controls[ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown2];

        type TWidgetType = EWidget.ratio | EWidget.bigNumber;

        const resultTypeMap = {
          [EWidget.bigNumber]: EWidgetRequestResultType.singleField,
          [EWidget.ratio]: EWidgetRequestResultType.singleField,
        };

        return {
          dropdownLabel:
            this.injectData.type === EWidget.bigNumber
              ? this.translate.instant('page.widgetContent.addEditDialog.widgetRequest')
              : this.translate.instant(
                  `page.widgetContent.addEditDialog.${type === ETypeDependentInputs.widgetRequestSingleSelectDropdown ? 'ratioWidgetRequest1' : 'ratioWidgetRequest2'}`,
                ),
          resultType: resultTypeMap[this.injectData.type as TWidgetType],
          type,
          widgetRequestCategoryFormControl,
        };
      case ETypeDependentInputs.text:
      case ETypeDependentInputs.text2:
        return {
          label: addEditWidgetInputConfiguration[type].editDataKeyName(this.injectData.type),
        };
      case ETypeDependentInputs.formSingleSelectDropdown:
        return {
          selectedIdOnEdit:
            (this.injectData.editData?.typeBasedConfiguration as TWidgetCustomParameters[EWidget.formIo] | undefined)
              ?.formId ?? null,
        };
      case ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown:
      case ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown2:
        return {
          dropdownLabel:
            this.injectData.type === EWidget.bigNumber
              ? this.translate.instant('page.widgetContent.addEditDialog.widgetRequestCategoryDropdown.label')
              : this.translate.instant(
                  `page.widgetContent.addEditDialog.${type === ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown ? 'ratioWidgetRequestCategory1' : 'ratioWidgetRequestCategory2'}`,
                ),
        };
      case ETypeDependentInputs.datatableConfiguration:
        const { availableStandardHeaders, defaultHeaders, store } = this.getDatatableConfigurationContext(
          this.injectData.type,
        );
        const isNotActionTrackerIssues: boolean = this.injectData.type !== EWidget.actionTrackerIssues;

        const pageConfigurationBuilder: (params: IPageConfigurationBuilderParams) => IPageConfigurationData = ({
          availableHeaders: [generalInformationHeaders, customHeaders],
          initiallySelectedHeaders,
        }) => {
          return {
            configurationTabs: [
              {
                availableColumnsBySections: [
                  ...(generalInformationHeaders.length
                    ? [
                        {
                          headers: generalInformationHeaders.map((row) => ({
                            ...row,
                            name: this.translate.instant(`field.${row.name}`),
                          })),
                          sectionTitle: this.translate.instant('system.dialogSection.generalInformation'),
                        },
                      ]
                    : []),
                  {
                    headers: customHeaders,
                    sectionTitle: this.translate.instant(
                      isNotActionTrackerIssues
                        ? 'system.dialogSection.generalInformation'
                        : 'system.dialogSection.itemFields',
                    ),
                  },
                ],
                initiallySelectedHeaders,
                lockedHeaders: this.getLockedHeaders(this.injectData.type),
                type: 'datatable',
                unselectedByDefaultHeaders: [],
              },
            ],
            isSetAsDefaultAvailable: false,
          };
        };

        return {
          availableStandardHeaders,
          defaultHeaders,
          pageConfigurationBuilder,
          store,
        };
      default:
        return {};
    }
  }

  public getBoard(search?: string): void {
    const boardId: number | null | undefined = this.injectData.editData?.configuration?.actionTracker?.boardId;

    this.store.loadBoards({
      fields: ['name'],
      limit: 20,
      ...(boardId && !search?.trim()
        ? {
            sort: [
              { active: `id = ${boardId}`, direction: 'desc' },
              { active: 'id', direction: 'desc' },
            ],
          }
        : {
            sort: [{ active: 'name', direction: 'asc' }],
          }),
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public getItemCategories(boardId: number, itemType: string): void {
    const filters: IGenericCrudRequestConstructionParameterFilters[] = [
      { field: 'boardId', ids: [boardId] },
      { field: 'itemType', ids: [itemType] },
    ];

    this.store.loadItemCategories({
      fields: ['itemCategory', 'boardId', 'itemType'],
      filters,
      join: ['itemCategory||name,isDefault,isActive'],
    });
  }

  private getActionTrackerConfigurationDropdowns(): void {
    const actionConfig: IActionTracker | undefined = this.injectData.editData?.configuration?.actionTracker;

    if (this.isActionTrackerModuleAllowed() && actionConfig?.boardId) {
      this.getBoard();
    }

    this.store.boardsDropdown$.subscribe((data) => (this.boards = data));
    this.store.boardsDropdown$
      .pipe(
        filter((data) => !!data?.length),
        take(1),
      )
      .subscribe((data) => {
        const matched: IDropdownOption<number>[] = data.filter((board) => board.id === actionConfig?.boardId);

        if (matched.length) {
          this.formGroup.controls.actionCreateDefaultBoardId.setValue(matched);
        }
      });

    this.store.itemCategoriesDropdown$.subscribe((data) => (this.itemCategories = data));
    this.store.itemCategoriesDropdown$
      .pipe(
        filter((data) => !!data?.length),
        take(1),
      )
      .subscribe((data) => {
        const matched: IDropdownOption<number>[] = data.filter((cat) => cat.id === actionConfig?.itemCategoryId);

        if (matched.length) {
          this.formGroup.controls.actionCreateDefaultItemCategoryId.setValue(matched);
        }
      });

    this.listenBoardAndItemTypeChanges();
  }

  private listenBoardAndItemTypeChanges(): void {
    const boardControl = this.formGroup.controls.actionCreateDefaultBoardId as FormControl<
      IDropdownOption<number>[] | null
    >;

    const itemTypeControl = this.formGroup.controls.actionCreateDefaultItemTypeId as FormControl<
      IDropdownOption<string>[] | null
    >;

    const itemCategoryControl: FormControl<IDropdownOption<number>[] | null> =
      this.formGroup.controls.actionCreateDefaultItemCategoryId;

    combineLatest([
      boardControl.valueChanges.pipe(startWith(boardControl.value)),
      itemTypeControl.valueChanges.pipe(startWith(itemTypeControl.value)),
    ]).subscribe(([boards, itemTypes]) => {
      const boardId: number | undefined = boards?.[0]?.id;
      const itemType: string | undefined = itemTypes?.[0]?.id;

      itemCategoryControl.setValue(null);

      if (!boardId || !itemType) {
        this.itemCategories = [];
        itemCategoryControl.disable();

        return;
      }

      itemCategoryControl.enable();
      this.getItemCategories(boardId, itemType);
    });
  }

  private disableBigNumberFormRelatedFieldsConditionally(): void {
    runInInjectionContext(this.injector, () => {
      this.isCategoryFormSubmissions$ = this.formGroup.controls[
        ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown
      ].valueChanges.pipe(
        startWith(this.formGroup.controls[ETypeDependentInputs.widgetRequestCategorySingleSelectDropdown].value),
        map((category) => Boolean(category?.length) && category[0].id === EWidgetRequestCategory.formSubmissions),
        distinctUntilChanged(),
      );

      const isFormDataTypeValueFromContent$ = this.formGroup.controls[
        ETypeDependentInputs.formDataType
      ].valueChanges.pipe(
        startWith(this.formGroup.controls[ETypeDependentInputs.formDataType].value),
        map(
          (formDataType) =>
            !this.formGroup.controls[ETypeDependentInputs.formDataType].disabled &&
            Boolean(formDataType?.length) &&
            formDataType[0].id === EFormDataType.valueFromContent,
        ),
        distinctUntilChanged(),
      );

      const isWidgetRequestSelected$ = this.formGroup.controls[
        ETypeDependentInputs.widgetRequestSingleSelectDropdown
      ].valueChanges.pipe(
        startWith(this.formGroup.controls[ETypeDependentInputs.widgetRequestSingleSelectDropdown].value),
        map((widgetRequest) => Boolean(widgetRequest.length)),
        distinctUntilChanged(),
      );

      this.isValueFromContentSetup$ = combineLatest({
        isCategoryFormSubmissions: this.isCategoryFormSubmissions$,
        isFormDataTypeValueFromContent: isFormDataTypeValueFromContent$,
        isWidgetRequestSelected: isWidgetRequestSelected$,
      }).pipe(
        map(
          ({ isCategoryFormSubmissions, isWidgetRequestSelected, isFormDataTypeValueFromContent }) =>
            isCategoryFormSubmissions && isWidgetRequestSelected && isFormDataTypeValueFromContent,
        ),
      );

      this.isCategoryFormSubmissions$.pipe(takeUntilDestroyed()).subscribe((isCategoryFormSubmissions) => {
        if (isCategoryFormSubmissions) {
          this.formGroup.controls[ETypeDependentInputs.formDataType].enable();
        } else {
          this.formGroup.controls[ETypeDependentInputs.formDataType].disable();
        }
      });

      this.isValueFromContentSetup$.pipe(takeUntilDestroyed()).subscribe((isValueFromContentSetup) => {
        if (isValueFromContentSetup) {
          this.formGroup.controls[ETypeDependentInputs.formField].enable();
          this.formGroup.controls[ETypeDependentInputs.resultAggregation].enable();
        } else {
          this.formGroup.controls[ETypeDependentInputs.formField].disable();
          this.formGroup.controls[ETypeDependentInputs.resultAggregation].disable();
        }
      });
    });
  }

  private initializeFilterSectionData(): void {
    this.filterSectionData = (this.filterModeOrder() ?? [])
      .filter((mode) => this.filtersOfWidget().some(({ name }) => name === mode))
      .map((modeName) => {
        const matchingModeSave = this.filters.find(({ modeName: modeNameFromSave }) => modeName === modeNameFromSave);
        const matchingFilterSuppression = this.filterSuppression().find(
          ({ modeName: modeNameFromSave }) => modeName === modeNameFromSave,
        );

        if (!matchingFilterSuppression) {
          this.filterSuppression.update((filterSuppression) => [
            ...filterSuppression,
            { modeName, suppressedFilters: [] },
          ]);
        }

        const filtersOfMode = this.filtersOfWidget().find(({ name }) => name === modeName)!.filters;

        return {
          filters: matchingModeSave
            ? filtersOfMode.map(({ identity }): IConfigurationsOfAdvancedFilterInputs => {
                const filterSave = matchingModeSave.filters.find(
                  (save) => save.field === identity.field && identity.operators.includes(save.operator),
                );

                return filterSave
                  ? {
                      inputsConfiguration: {
                        fixedField: filterSave.field,
                        initialValues: filterSave,
                        isEqAllowed: true,
                        mode: EAdvancedFilterInputMode.autoSubmit,
                        type: 'edit',
                      },
                      selectedFilterIdentity: signal<IFilterIdentity | null>(null),
                      submitData: signal<TAdvancedFilterInputsAutoSubmit | null>(null),
                    }
                  : {
                      inputsConfiguration: {
                        fixedField: identity.field,
                        isEqAllowed: true,
                        mode: EAdvancedFilterInputMode.autoSubmit,
                        type: 'add',
                      },
                      selectedFilterIdentity: signal<IFilterIdentity | null>(null),
                      submitData: signal<TAdvancedFilterInputsAutoSubmit | null>(null),
                    };
              })
            : this.filtersOfWidget()
                .find(({ name }) => name === modeName)!
                .filters.map(
                  ({ identity }): IConfigurationsOfAdvancedFilterInputs => ({
                    inputsConfiguration: {
                      fixedField: identity.field,
                      isEqAllowed: true,
                      mode: EAdvancedFilterInputMode.autoSubmit,
                      type: 'add',
                    },
                    selectedFilterIdentity: signal<IFilterIdentity | null>(null),
                    submitData: signal<TAdvancedFilterInputsAutoSubmit | null>(null),
                  }),
                ),
          matchingFilterSuppression: this.filterSuppression().find(
            ({ modeName: modeNameFromSave }) => modeName === modeNameFromSave,
          )!.suppressedFilters,
          modeName,
        };
      });

    runInInjectionContext(this.injector, () => {
      effect(() => {
        const filterModeOrder = this.filterModeOrder();
        this.filterSuppression.update((currentValue) => {
          return filterModeOrder.map((modeName) => ({
            modeName,
            suppressedFilters: currentValue.find((value) => value.modeName === modeName)?.suppressedFilters ?? [],
          }));
        });

        this.filterSectionData = filterModeOrder.reduce((reducePayload, modeName) => {
          const matchingSection = this.filterSectionData.find((section) => section.modeName === modeName);

          return [
            ...reducePayload,
            matchingSection ?? {
              filters: this.filtersOfWidget()
                .find(({ name }) => name === modeName)!
                .filters.map(
                  ({ identity }): IConfigurationsOfAdvancedFilterInputs => ({
                    inputsConfiguration: {
                      fixedField: identity.field,
                      isEqAllowed: true,
                      mode: EAdvancedFilterInputMode.autoSubmit,
                      type: 'add',
                    },
                    selectedFilterIdentity: signal<IFilterIdentity | null>(null),
                    submitData: signal<TAdvancedFilterInputsAutoSubmit | null>(null),
                  }),
                ),
              matchingFilterSuppression: untracked(this.filterSuppression).find(
                (suppression) => suppression.modeName === modeName,
              )!.suppressedFilters,
              modeName,
            },
          ];
        }, [] as IFilterPanel[]);
      });
    });
  }

  private getDatatableConfigurationContext<TStore extends ComponentStore<any>>(
    widgetType: EWidget,
  ): IDatatableConfigurationContext<TStore> {
    const configMap: Partial<Record<EWidget, IDatatableConfigurationContext<any>>> = {
      [EWidget.lineOverview]: {
        availableStandardHeaders: [],
        defaultHeaders: defaultLineOverviewColumns,
        store: this.lineOverviewDatatableConfigurationStore,
      },
    };

    return (
      (configMap[widgetType] as IDatatableConfigurationContext<TStore>) ?? {
        availableStandardHeaders: itemsDatatableColumns,
        defaultHeaders: itemsDatatableDefaultColumns,
        store: this.actionTrackerItemsDatatableConfigurationStore,
      }
    );
  }

  private getLockedHeaders(widgetType: EWidget): number[] {
    // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
    switch (widgetType) {
      case EWidget.lineOverview:
        return [ELineOverviewColumn.line];
      case EWidget.actionTrackerIssues:
        return [-2];
      default:
        return [];
    }
  }
}
